import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CircularProgress, Tooltip } from "@mui/material";
import { useState } from "react";
import "../../style/ActionButton.css";

// reusable action button with variants and icon-only mode
const ActionButton = ({
  children,
  onClick,
  variant = "primary", // primary (green filled), secondary (green outlined), danger (red filled), default (grey outlined)
  modal = false, // applies fixed width for modal dialogs (150px desktop, 100% mobile)
  icon, // optional FontAwesome icon
  iconOnly = false, // icon-only mode (no text, compact size, requires tooltip)
  tooltip, // tooltip text (required when iconOnly=true)
  disabled = false,
  loading = false, // shows spinner and disables button
  spinning = false, // spin the icon (for refresh)
  flashOnClick = false, // flash green temporarily on click
  className = "",
  style = {},
  ...props
}) => {
  const [isFlashing, setIsFlashing] = useState(false);

  // use success variant when spinning or flashing
  const effectiveVariant = spinning || isFlashing ? "success" : variant;

  const variantClass = `action-button--${effectiveVariant}`;
  const modalClass = modal ? "action-button--modal" : "";
  const iconOnlyClass = iconOnly ? "action-button--icon-only" : "";

  const handleClick = (e) => {
    if (disabled || loading) return;

    // trigger flash effect if enabled
    if (flashOnClick && !spinning && !loading) {
      setIsFlashing(true);
      setTimeout(() => setIsFlashing(false), 1000);
    }

    // call the original onClick handler
    if (onClick) {
      onClick(e);
    }
  };

  const button = (
    <button
      className={`action-button ${variantClass} ${modalClass} ${iconOnlyClass} ${className}`.trim()}
      onClick={handleClick}
      disabled={disabled || loading}
      style={style}
      {...props}
    >
      {/* show icon when provided and not loading */}
      {icon && !loading && (
        <FontAwesomeIcon 
          icon={icon} 
          className="action-button__icon" 
          spin={spinning}
        />
      )}
      {/* show text when not icon-only mode */}
      {!iconOnly && children && (
        <span className="action-button__text">{children}</span>
      )}
      {/* show spinner when loading */}
      {loading && (
        <CircularProgress size={16} className="action-button__spinner" />
      )}
    </button>
  );

  // wrap in tooltip if provided (required for icon-only mode)
  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {button}
      </Tooltip>
    );
  }

  return button;
};

ActionButton.propTypes = {
  children: PropTypes.node, // not required when iconOnly=true
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(["primary", "secondary", "danger", "default", "success", "warning"]),
  modal: PropTypes.bool,
  icon: PropTypes.object, // FontAwesome icon object
  iconOnly: PropTypes.bool,
  tooltip: PropTypes.string, // required when iconOnly=true
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  spinning: PropTypes.bool,
  flashOnClick: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default ActionButton;

// Usage examples:
//
// 1. Standard button with text:
// <ActionButton variant="primary" icon={faSave}>Save Changes</ActionButton>
//
// 2. Icon-only toolbar button:
// <ActionButton variant="default" icon={faSync} iconOnly tooltip="Refresh" spinning={isRefreshing} />
//
// 3. Flash on click (export):
// <ActionButton variant="default" icon={faDownload} iconOnly tooltip="Export" flashOnClick />
//
// 4. Modal button:
// <ActionButton variant="danger" modal>Delete</ActionButton>